import { useRef, useState } from 'react';
import { useDrag, useDrop } from 'react-dnd';
import { Box, Collapse, IconButton, Typography } from '@mui/material';
import DragIndicatorIcon from '@mui/icons-material/DragIndicator';
import AddCircleIcon from '@mui/icons-material/AddCircle';
import RemoveCircleIcon from '@mui/icons-material/RemoveCircle';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import OptionEntry from './OptionEntry';
import { SurveyV2 } from '@act/shared/data-transfer-objects';

interface IDraggableOptionEntryProps {
  id: string;
  index: number;
  questionKey: string;
  option: SurveyV2.CreateOptionJSONDto;
  questionCount: number;
  isActionSurvey: boolean;
  handleMoveOption: (dragIndex: number, hoverIndex: number) => void;
  handleUpdateOption: (option: SurveyV2.CreateOptionJSONDto) => void;
  handleAddOption: (sortOrder: number) => void;
  handleRemoveOption: (sortOrder: number) => void;
  formErrors?: any; // Form validation errors from React Hook Form
  questionIndex?: number; // Index of this question in the questions array
  optionIndex?: number; // Index of this option in the question's options array
}

interface DragItem {
  index: number;
  id: string;
  questionKey: string;
  type: string;
}

const DraggableOptionEntry = ({
  id,
  index,
  questionKey,
  option,
  questionCount,
  isActionSurvey,
  handleMoveOption,
  handleUpdateOption,
  handleAddOption,
  handleRemoveOption,
  formErrors,
  questionIndex,
  optionIndex,
}: IDraggableOptionEntryProps) => {
  const [open, setOpen] = useState(false);

  const ref = useRef<HTMLDivElement>(null);

  const [{ handlerId }, drop] = useDrop<
    DragItem,
    void,
    { handlerId: string | symbol | null }
  >({
    accept: 'option',
    collect(monitor) {
      return {
        handlerId: monitor.getHandlerId(),
      };
    },
    hover(item: DragItem, monitor) {
      if (!ref.current) {
        return;
      }

      if (item.questionKey !== questionKey) {
        return;
      }

      const dragIndex = item.index;
      const hoverIndex = index;

      if (dragIndex === hoverIndex) {
        return;
      }

      const hoverBoundingRect = ref.current?.getBoundingClientRect();
      const hoverMiddleY =
        (hoverBoundingRect.bottom - hoverBoundingRect.top) / 2;
      const clientOffset = monitor.getClientOffset();
      const hoverClientY = clientOffset!.y - hoverBoundingRect.top;

      if (dragIndex < hoverIndex && hoverClientY < hoverMiddleY) {
        return;
      }
      if (dragIndex > hoverIndex && hoverClientY > hoverMiddleY) {
        return;
      }

      handleMoveOption(dragIndex, hoverIndex);
      item.index = hoverIndex;
    },
  });

  const [{ isDragging }, drag] = useDrag({
    type: 'option',
    item: () => {
      return { id, index, questionKey };
    },
    collect: monitor => ({
      isDragging: monitor.isDragging(),
    }),
  });

  const opacity = isDragging ? 0.4 : 1;
  drag(drop(ref));

  const onToggle = () => setOpen(!open);

  return (
    <Box
      ref={ref}
      data-handler-id={handlerId}
      sx={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        opacity,
        cursor: 'move',
        width: '100%',
        boxSizing: 'border-box',
      }}
    >
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          width: '100%',
          marginBottom: '16px',
        }}
      >
        <DragIndicatorIcon
          sx={{ marginRight: '8px', color: 'text.secondary' }}
        />
        <Typography variant="h6" sx={{ color: 'text.primary' }}>
          {option.text ? option.text : `Option ${option.sortOrder}`}
        </Typography>
        <IconButton
          sx={{
            height: '40px',
            width: '40px',
            marginLeft: 'auto',
            '&:hover': {
              backgroundColor: 'rgba(255,105,97,.25)',
            },
          }}
          onClick={() => handleRemoveOption(option.sortOrder)}
        >
          <RemoveCircleIcon />
        </IconButton>
        <IconButton
          sx={{
            height: '40px',
            width: '40px',
            '&:hover': {
              backgroundColor: 'rgba(119,221,119,.25)',
            },
          }}
          onClick={() => handleAddOption(option.sortOrder)}
        >
          <AddCircleIcon />
        </IconButton>
        <IconButton onClick={onToggle}>
          {open ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}
        </IconButton>
      </Box>
      <Collapse in={open} unmountOnExit sx={{ width: '100%' }}>
        <OptionEntry
          option={option}
          questionKey={questionKey}
          questionCount={questionCount}
          isActionSurvey={isActionSurvey}
          handleUpdateOption={handleUpdateOption}
          formErrors={formErrors}
          questionIndex={questionIndex}
          optionIndex={optionIndex}
        />
      </Collapse>
    </Box>
  );
};

export default DraggableOptionEntry;
